/**
 * Page state that lives in the URL (OPEN-191 / OPEN-204): tabs, in-page details and table
 * sorts are query parameters, so browser Back, a refresh and a shared link restore them. The
 * helpers live in web-shared for every web app; this binds them to web-dispatch's router.
 *
 * Values equal to their default are left out of the URL, and changes REPLACE the history entry.
 * One setter call per handler: a second write in the same tick starts from the stale params and
 * undoes the first, so a change that must clear other params names them in `resets`.
 * See web-shared `lib/url-state.ts`.
 */

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createUrlState } from '@lodex-pro/web-shared';

export {
  ariaSort,
  listFrom,
  sortRows,
  type ListFromState,
  type SortState,
} from '@lodex-pro/web-shared';

export const { useUrlParam, useUrlFlag, useUrlNumber, useBackTo, useUrlSort } = createUrlState({
  useSearchParams,
  useLocation,
});

/** Marks a history entry this page pushed to open a detail, so its in-page Back can pop it. */
interface DetailOpenedState {
  readonly urlDetail: string;
}

/**
 * OPEN-191 — the id of a detail shown in place of its list (`?well=<id>`). Opening one PUSHES a
 * history entry, so browser Back closes it. The in-page Back (`null`) then goes back that one
 * step too, so the two Backs agree and the history holds no second copy of the list. A detail
 * reached any other way (a link, a refresh) has no such entry, and closing it just removes the
 * param. Other params (the list's filters) are kept either way.
 */
export function useUrlDetail(key: string): [string | null, (id: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const raw = params.get(key);
  const set = (id: string | null): void => {
    const closing = id === null || id === '';
    const opened = (location.state as Partial<DetailOpenedState> | null)?.urlDetail === key;
    if (closing && opened) {
      void navigate(-1);
      return;
    }
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (closing) p.delete(key);
        else p.set(key, id);
        return p;
      },
      closing ? { replace: true } : { state: { urlDetail: key } satisfies DetailOpenedState },
    );
  };
  return [raw === null || raw === '' ? null : raw, set];
}
